import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  AG_GRID_OPTION_GROUP_KEY,
  BlotterTable,
  Box,
  intersectTwoSets,
  LoaderTalos,
  useBlotterTable,
  useDynamicCallback,
  type AgGridOptionContext,
  type AgGridOptionGroupCellProps,
  type IPositionUpdate,
  type SubscriptionResponse,
} from '@talos/kyoko';
import type { FirstDataRenderedEvent, GridOptions, ICellRendererParams, RowClassRules } from 'ag-grid-community';
import Big from 'big.js';
import { memo, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import type { Observable } from 'rxjs';
import type { OptionMarketTabData } from '../../../../providers/MarketTabs.types';
import {
  selectColumnVisibility,
  selectOptionFilter,
  selectOptionMarketSelected,
  selectStrikeFilter,
} from './Filter/selectors';
import { useCrossStrikeByExpiry } from './hooks/useCrossStrikeByExpiry';
import { useOptionChainColumns } from './hooks/useOptionChainColumns';
import { useOptionChainData } from './hooks/useOptionChainData';
import { useOptionChainPrimeOms } from './hooks/useOptionChainPrimeOms';
import { useOptionChainRowFilter } from './hooks/useOptionChainRowFilter';
import { PRICE_ABOVE_STRIKE_CLASSNAME, PRICE_BELOW_STRIKE_CLASSNAME, type OptionChainDataRow } from './types';
import { useGetOptionChainContextMenu } from './useGetOptionChainContextMenu';
import { formatOptionExpiry, getColumnVisibilityArrays, isRfqOnlyOptionMarket } from './utils';

export const OptionsBlotter = memo(
  ({
    positionsSubscription,
    initialColumnDefsVisibility,
    panelID,
  }: {
    positionsSubscription: Observable<SubscriptionResponse<IPositionUpdate>>;
    initialColumnDefsVisibility?: OptionMarketTabData['columnDefsVisibility'];
    panelID: string;
  }) => {
    const strikeFilter = useAppStateSelector(state => selectStrikeFilter(state, panelID));
    const optionFilter = useAppStateSelector(state => selectOptionFilter(state, panelID));
    const { dataObservable, expiryToStrikePriceToIndex, underlyingPriceSubscription } = useOptionChainData({
      tag: 'OptionsBlotter',
      optionFilter: optionFilter,
      strikeFilter: strikeFilter,
      positionsSubscription: positionsSubscription,
      panelID,
    });

    const crossStrikeByExpiry = useCrossStrikeByExpiry({
      expiryToStrikePriceToIndex,
      underlyingPriceSubscription,
      panelID,
    });

    const columns = useOptionChainColumns(initialColumnDefsVisibility);
    const strikeRowFilter = useOptionChainRowFilter({ crossStrikeByExpiry, expiryToStrikePriceToIndex, panelID });
    const primeOMSOnCellClick = useOptionChainPrimeOms({ panelID });

    const [showLoader, setShowLoader] = useState(true);
    /**
     *  Ensures that strike column is centered at first data rendered.
     */
    const handleOnFirstDataRendered = useDynamicCallback((event: FirstDataRenderedEvent<OptionChainDataRow>) => {
      // Center strike price after a slight delay. We need to happen after columns autosizing (flex).
      setTimeout(() => {
        event.api.ensureColumnVisible('StrikePrice', 'middle');
        setShowLoader(false);
      }, 50);
    });

    const blotterTableContext: AgGridOptionContext = useMemo(() => {
      return { crossStrikeByExpiry };
    }, [crossStrikeByExpiry]);
    const getCustomContextMenuItems = useGetOptionChainContextMenu(panelID);

    /**
     * Based on row strike, determines if option is regarded as ITM/OTM, and provides css-class accordingly.
     * Each cell has classnames PUT or CALL, and given the row class, they are colored blue or not.
     */
    const rowClassRules: RowClassRules<OptionChainDataRow> = useMemo(() => {
      return {
        [PRICE_ABOVE_STRIKE_CLASSNAME]: ({ data }) => {
          const rowStrike = data?.StrikePrice;
          const rowExpiry = data?.Expiration;
          const rowUnderlying = data?.UnderlyingPrice ?? (rowExpiry ? crossStrikeByExpiry.get(rowExpiry) : undefined);

          return Boolean(rowStrike && rowUnderlying && Big(rowStrike).gt(rowUnderlying));
        },
        [PRICE_BELOW_STRIKE_CLASSNAME]: ({ data }) => {
          const rowStrike = data?.StrikePrice;
          const rowExpiry = data?.Expiration;
          const rowUnderlying = data?.UnderlyingPrice ?? (rowExpiry ? crossStrikeByExpiry.get(rowExpiry) : undefined);

          return Boolean(rowStrike && rowUnderlying && !Big(rowStrike).gt(rowUnderlying));
        },
      };
    }, [crossStrikeByExpiry]);

    const selectedMarketName = useAppStateSelector(state => selectOptionMarketSelected(state, panelID));
    const shouldPrimeOMSOnCellClick = !isRfqOnlyOptionMarket(selectedMarketName);

    const gridOptions: GridOptions<OptionChainDataRow> = useMemo(
      () => ({
        onFirstDataRendered: handleOnFirstDataRendered,
        suppressMovableColumns: true,
        rowClassRules,
        groupDefaultExpanded: 1,
        // https://www.ag-grid.com/react-data-grid/full-width-rows/
        isFullWidthRow: params => {
          return params.rowNode.level === 0;
        },
        fullWidthCellRenderer: AG_GRID_OPTION_GROUP_KEY,
        fullWidthCellRendererParams: (params: ICellRendererParams): AgGridOptionGroupCellProps => {
          const expiry = params.value;
          const formattedExpiration = expiry ? formatOptionExpiry(expiry) : expiry;
          return { formattedExpiration };
        },
        onCellClicked: shouldPrimeOMSOnCellClick ? primeOMSOnCellClick : undefined,
        groupDisplayType: 'custom',
      }),
      [handleOnFirstDataRendered, shouldPrimeOMSOnCellClick, primeOMSOnCellClick, rowClassRules]
    );

    const blotterTable = useBlotterTable({
      dataObservable,
      errorObservable: undefined,
      rowID: 'rowID',
      columns,
      clientLocalFilter: strikeRowFilter,
      // Required for grouping by expiries https://www.ag-grid.com/react-data-grid/grouping-custom-group-columns/
      context: blotterTableContext,
      contextMenuOptions: {
        getCustomContextMenuItems,
      },
      gridOptions: gridOptions,
    });

    const columnDefsVisibility = useAppStateSelector(state => selectColumnVisibility(state, panelID));

    const centerStrikePrice = useDynamicCallback(() => {
      blotterTable.scrollVerticallyToColumn('StrikePrice', 'middle');
    });
    // Update the ag grid instance with the column selector visibility
    useEffect(() => {
      // Put a timeout on this to ensure that loader can display first if needed
      const timeout = setTimeout(() => {
        const { visible, hidden } = getColumnVisibilityArrays(columnDefsVisibility, selectedMarketName);
        const columns = blotterTable.gridApi?.getColumns();
        if (!columns) {
          return;
        }
        const currentVisibileColumns = columns.filter(column => column.isVisible()).map(column => column.getColId());
        const currentHiddenColumns = columns.filter(column => !column.isVisible()).map(column => column.getColId());

        const currentVisibleSet = new Set(currentVisibileColumns);
        currentVisibleSet.delete('Expiration');
        currentVisibleSet.delete('StrikePrice');

        const currentHiddenSet = new Set(currentHiddenColumns);
        currentHiddenSet.delete('Expiration');
        currentHiddenSet.delete('StrikePrice');

        const newVisibleSet = new Set(visible);
        const newHiddenSet = new Set(hidden);
        const visibleIntersection = intersectTwoSets(currentVisibleSet, newVisibleSet);
        const hiddenIntersection = intersectTwoSets(currentHiddenSet, newHiddenSet);

        const visibleIsSame =
          visibleIntersection.size === currentVisibleSet.size && visibleIntersection.size === newVisibleSet.size;
        const hiddenIsSame =
          hiddenIntersection.size === currentHiddenSet.size && hiddenIntersection.size === newHiddenSet.size;

        if (!visibleIsSame && !hiddenIsSame) {
          blotterTable.setColumnsVisible(visible, true);
          blotterTable.setColumnsVisible(hidden, false);
        }
      }, 0);
      return () => clearTimeout(timeout);
    }, [columnDefsVisibility, blotterTable, selectedMarketName]);

    useEffect(() => {
      // Whenever cme columns changes:
      // 1. Show loader
      // 2. re-center strike prices
      // 3. Remove loader
      setShowLoader(true);
      const centerStrikesTimeout = setTimeout(() => {
        centerStrikePrice();
      }, 100);
      const loaderTimeout = setTimeout(() => {
        setShowLoader(false);
      }, 300);

      return () => {
        clearTimeout(loaderTimeout);
        clearTimeout(centerStrikesTimeout);
        setShowLoader(false);
      };
    }, [selectedMarketName, centerStrikePrice]);

    return (
      <Box w="100%" h="100%" position="relative">
        <OptionsLoader showLoader={showLoader} />
        <BlotterTable {...blotterTable} />
      </Box>
    );
  },
  // Check if reference is equal
  (p, n) => p.positionsSubscription === n.positionsSubscription
);

/**
 * Due to resizing of columns and centering of strike prices, we render a loading spinner before
 * the first data has rendered.
 */
const OptionsLoader = ({ showLoader }: { showLoader: boolean }) => {
  const [debouncedLoading, setDebouncedLoading] = useState<boolean>(showLoader);
  useDebounce(() => setDebouncedLoading(showLoader), 200, [showLoader]);

  if (!debouncedLoading && !showLoader) {
    return null;
  }

  return (
    <Box zIndex={1} background="backgroundBlotterTableRow" position="absolute" top="0" left="0" bottom="0" right="0">
      <LoaderTalos />
    </Box>
  );
};
